import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual } from 'lodash';

import CreatorTilePagination from 'components/creator-tile/pagination/CreatorTilePagination';
import CreatorTileInteraction from 'components/creator-tile/interaction/CreatorTileInteraction';
import BackToTopControl from 'components/back-to-top-control/BackToTopControl';
import TileStandardLoading from 'components/tiles/standard/loading/TileStandardLoading';

import DiscoveryCreatorsResultsAddLightweight from './lightweight/DiscoveryCreatorsResultsAddLightweight';
import DiscoveryCreatorsResultsEmpty from './empty/DiscoveryCreatorsResultsEmpty';
import DiscoveryCreatorsResultsTiles from './tiles/DiscoveryCreatorsResultsTiles';

import { resetDiscoveryCreators, resetDiscoveryCreatorsFilters } from 'modules/discovery/creators/reducers/creators';
import { fetchInfluencersRecentPosts } from 'modules/influencers/reducers/influencerRecentPosts';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';
import { fetchInfluencersHistory } from 'modules/lists/reducers/lists';

import {
  getArrayOfSelectedChannels,
  getChannel,
  getCreatorIdsGroupedByMetricChannels,
  getTileSize,
  setTileSize
} from 'modules/discovery/creators/utils/creators';
import { CHANNELS_WITH_METRICS } from 'utils/channels';
import { cancelAllCancelablePromises } from 'modules/influencers/utils/promise-handler';
import { getMostActiveState } from 'components/tabs/tabs-underlined/tabs-underlined-utils';

import type {
  DiscoveryCreatorsFilters,
  DiscoveryScreenState,
  InfluencerTile as InfluencerTileType,
  Organization
} from 'types';
import type { RecentPostsDataType } from 'modules/influencers/types/RecentPosts.type';

type OwnProps = {
  creators: Array<InfluencerTileType>,
  isLoading: boolean,
  isSavingToList: boolean,
  isFreemium: boolean,
  onFetchCreators: (a?: number | null) => void,
  onSaveCreatorsToList: (e: string, d: string, c: number, b: DiscoveryCreatorsFilters, a: string) => void
};

type StateProps = {
  currentOrganization: Organization,
  filters: DiscoveryCreatorsFilters,
  screenState: DiscoveryScreenState,
  influencerRecentPosts: {
    [x: number]: RecentPostsDataType
  }
};

type DispatchProps = {
  dispatchFetchInfluencersMeta: (a: Array<number>) => void,
  dispatchFetchInfluencersRecentPosts: (
    e: Array<number>,
    d: string,
    c: DiscoveryCreatorsFilters,
    b: number,
    a: boolean
  ) => void,
  dispatchFetchInfluencersHistory: (b: number, a: Array<number>) => void,
  dispatchResetCreators: () => void,
  dispatchResetFilters: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  expandedIdsMap: {
    [type: number]: string
  }
};

const EXPANDED = 'expanded';
const COLLAPSED = 'collapsed';

class DiscoveryCreatorsResults extends React.PureComponent<Props, State> {
  state = {
    expandedIdsMap: {}
  };

  containerRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.setExpandedState(this.getGlobalTileSize());
  }

  componentDidUpdate(prevProps: Props) {
    const {
      filters,
      creators,
      currentOrganization,
      dispatchFetchInfluencersMeta,
      dispatchFetchInfluencersHistory,
      dispatchFetchInfluencersRecentPosts
    } = this.props;
    const { expandedIdsMap } = this.state;

    const creatorsList = creators.map(creator => creator.id);
    const prevCreatorList = prevProps.creators.map(creator => creator.id);

    const hasNewCreatorsList = !isEmpty(creatorsList) && !isEqual(creatorsList, prevCreatorList);

    const channelToShow = this.getActiveChannelWithMetrics();

    if (hasNewCreatorsList) {
      dispatchFetchInfluencersMeta(creatorsList);
      dispatchFetchInfluencersHistory(currentOrganization.id, creatorsList);
    }

    if (hasNewCreatorsList || !isEqual(filters, prevProps.filters)) {
      cancelAllCancelablePromises();
      if (channelToShow) {
        dispatchFetchInfluencersRecentPosts(creatorsList, channelToShow, filters, 1, true);
      } else {
        // group creators by first visible channel and fetch them separately
        const groupedCreatorIds = getCreatorIdsGroupedByMetricChannels(creators);

        Object.keys(groupedCreatorIds).forEach((channel) => {
          dispatchFetchInfluencersRecentPosts(
            groupedCreatorIds[channel],
            channel,
            filters,
            1,
            false
          );
        });
      }
    }

    if (!isEqual(creators, prevProps.creators) && creators.length) {
      this.setExpandedState(getMostActiveState(expandedIdsMap) || this.getGlobalTileSize());
    }
  }

  componentWillUnmount() {
    cancelAllCancelablePromises();
    this.handleResetCreators();
  }

  setExpandedState(value: string): void {
    const { creators } = this.props;

    this.setState({
      expandedIdsMap: creators.reduce((res, creator) => {
        res[creator.id] = value;

        return res;
      }, {})
    });
  }

  getGlobalTileSize = () => (getTileSize('search') === EXPANDED ? EXPANDED : COLLAPSED);

  getActiveChannelWithMetrics(): string | undefined | null {
    return getArrayOfSelectedChannels(this.props.filters.channels)
      .filter(channel => CHANNELS_WITH_METRICS.includes(channel))[0];
  }

  getCurrentChannel(): string {
    const { filters } = this.props;

    return getChannel(filters.channels);
  }

  handleFetchCreatorRecentPosts = (id: number, channel: string, refetch: boolean) => {
    const { influencerRecentPosts, filters, dispatchFetchInfluencersRecentPosts } = this.props;
    const page = (refetch)
      ? 1
      : (influencerRecentPosts[id]?.lastFetchedPage || 1) + 1;

    const isCurrentlyLoading = influencerRecentPosts[id]?.isLoading;

    if (page === 1 || (page > 1 && influencerRecentPosts[id]?.hasMorePages && !isCurrentlyLoading)) {
      dispatchFetchInfluencersRecentPosts([id], channel, filters, page, true);
    }
  };

  handleResetCreators = (): void => {
    const { dispatchResetCreators } = this.props;

    dispatchResetCreators();
  };

  handleGlobalResize = (value: string): void => {
    this.setExpandedState(value);
    setTileSize('search', value);
  };

  handleSaveCreatorsToList = (label: string, description: string, orgId: number): void => {
    const { filters, screenState, onSaveCreatorsToList } = this.props;

    onSaveCreatorsToList(label, description, orgId, filters, screenState.sort);
  };

  handlePagination = (page: number): void => {
    const { onFetchCreators } = this.props;

    onFetchCreators(page);

    if (this.containerRef.current) {
      this.containerRef.current.scrollIntoView();
    }
  };

  handleResetFilters = (): void => {
    const { dispatchResetFilters } = this.props;

    dispatchResetFilters();
  };

  handleCreatorTileSizeChange = (value: string, creatorId: number) => {
    this.setState((prevState) => ({
      expandedIdsMap: {
        ...prevState.expandedIdsMap,
        [creatorId]: value
      }
    }));
  };

  renderNonResults(): React.ReactNode {
    const { isLoading } = this.props;
    const { expandedIdsMap } = this.state;

    if (isLoading) {
      return (
        <TileStandardLoading
          tileSize={getMostActiveState(expandedIdsMap) || this.getGlobalTileSize()}
        />
      );
    }

    return (
      <div className="discovery-creators-results__empty">
        <DiscoveryCreatorsResultsEmpty
          onResetFilters={this.handleResetFilters}
        />
        <DiscoveryCreatorsResultsAddLightweight />
      </div>
    );
  }

  renderBackToTop(): React.ReactNode {
    const { creators, isLoading } = this.props;

    return (
      <BackToTopControl
        isLoading={isLoading}
        show={creators.length > 4}
      />
    );
  }

  renderPagination(): React.ReactNode {
    const { screenState, isLoading } = this.props;

    return (
      <CreatorTilePagination
        meta={screenState.meta}
        links={screenState.links}
        isLoading={isLoading}
        onClick={this.handlePagination}
      />
    );
  }

  renderTopBar(): React.ReactNode {
    const { currentOrganization, isSavingToList, isFreemium, screenState } = this.props;
    const { expandedIdsMap } = this.state;

    return (
      <CreatorTileInteraction
        type="search"
        orgId={currentOrganization.id}
        totalItems={screenState.totalItems}
        sort={screenState.sort}
        isSavingToList={isSavingToList}
        isFreemium={isFreemium}
        onGlobalResize={this.handleGlobalResize}
        onSaveSearchToList={this.handleSaveCreatorsToList}
        toggleIdsMap={expandedIdsMap}
      />
    );
  }

  renderTiles(): React.ReactNode {
    const { creators, influencerRecentPosts, isFreemium } = this.props;
    const { expandedIdsMap } = this.state;
    const channel = this.getActiveChannelWithMetrics();
    const globalTileSize = this.getGlobalTileSize();

    return (
      <DiscoveryCreatorsResultsTiles
        creators={creators}
        influencerRecentPosts={influencerRecentPosts}
        channel={channel}
        expandedIdsMap={expandedIdsMap}
        isFreemium={isFreemium}
        updateCreatorTileSize={this.handleCreatorTileSizeChange}
        onResetCreators={this.handleResetCreators}
        onFetchInfluencerPosts={this.handleFetchCreatorRecentPosts}
        globalTileSize={globalTileSize}
      />
    );
  }

  renderResults(): React.ReactNode {
    const { isFreemium } = this.props;

    return (
      <React.Fragment>
        {this.renderTiles()}
        {
          !isFreemium && (
            this.renderPagination()
          )
        }
        {this.renderBackToTop()}
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { creators, isLoading } = this.props;

    return (
      <div
        ref={this.containerRef}
        className="discovery-creators-results"
        data-qa="discovery-creators-results"
      >
        <div className="discovery-creators-results__results">
          {this.renderTopBar()}
          {
            isLoading || isEmpty(creators)
              ? this.renderNonResults()
              : this.renderResults()
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ organizations, discoveryCreators, influencerRecentPosts }): StateProps => ({
  currentOrganization: organizations.currentOrganization,
  filters: discoveryCreators.filters,
  screenState: discoveryCreators.screenState,
  influencerRecentPosts: influencerRecentPosts.recentPostsData
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchInfluencersMeta: (creatorsList: Array<number>) => (
    dispatch(fetchInfluencersMeta(creatorsList))
  ),

  dispatchFetchInfluencersRecentPosts: (creatorsList: Array<number>, channelType: string, filters: DiscoveryCreatorsFilters, page: number, isCancelable: boolean) => (
    dispatch(fetchInfluencersRecentPosts(creatorsList, channelType, filters, page, isCancelable, true))
  ),

  dispatchFetchInfluencersHistory: (orgId: number, creatorsList: Array<number>) => (
    dispatch(fetchInfluencersHistory(orgId, creatorsList))
  ),

  dispatchResetCreators: () => dispatch(resetDiscoveryCreators()),
  dispatchResetFilters: () => dispatch(resetDiscoveryCreatorsFilters())
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryCreatorsResults);
